import { useEffect, useRef } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import DataTable, { IProps } from './DataTable';
import { shouldShowLoader } from './helpers';
import styles from './dataTable.module.scss';

export interface IStickyProps extends IProps {
  /**
   * CSS class name for the overlay that holds the sticky-left columns.
   * Allows custom styling of the sticky table container.
   */
  stickyTableContainerClassName?: string,
  /**
   * CSS class name for the container that holds the DataTable.
   * Provides custom styling for the data table container.
   */
  stickyDataTableContainer?: string,
  /**
   * Unique identifier for testing purposes.
   */
  dataTest: string,
}

const StickyDataTable = ({
  columns,
  data,
  dataTest,
  scrollContainerClassName,
  stickyDataTableContainer,
  stickyTableContainerClassName,
  loadingComponent: LoadingComponent,
  ...otherProps
}: Omit<IStickyProps, 'dataTableRef'>) => {
  const dataTableRef = useRef<HTMLDivElement>(null);
  const stickyDataTableRef = useRef<HTMLDivElement>(null);
  const stickyWrapperRef = useRef<HTMLDivElement>(null);

  const updateStickyDataTable = () => {
    if (!dataTableRef.current || !stickyDataTableRef.current || !stickyWrapperRef.current) {
      return;
    }

    const stickyTableColumnsCount = columns.filter((column) => column.stickyLeft).length;
    const tableRows = dataTableRef.current.children[0]?.querySelectorAll('tr');
    const stickyTableRows = stickyDataTableRef.current.children[0]?.querySelectorAll('tr');

    tableRows?.forEach((row, rowIndex) => {
      const stickyRow = stickyTableRows?.[rowIndex];

      if (!stickyRow) return;

      const stickyCells: NodeListOf<HTMLElement> = stickyRow.querySelectorAll('th, td');

      row.querySelectorAll('th, td').forEach((cell, cellIndex) => {
        const stickyCell = stickyCells[cellIndex];

        if (cellIndex >= stickyTableColumnsCount || !stickyCell) return;

        const { width, height } = cell.getBoundingClientRect();
        stickyCell.style.width = `${ width }px`;
        stickyCell.style.height = `${ height }px`;
      });
    });

    if (dataTableRef.current.clientWidth >= dataTableRef.current.scrollWidth) {
      // When left of wrapping parent is out of view
      stickyWrapperRef.current.classList.remove(styles.isVisible);
    } else {
      // When left of wrapping parent is in view
      stickyWrapperRef.current.classList.add(styles.isVisible);
    }
  };

  const throttledUpdateStickyDataTable = useRef(throttle(updateStickyDataTable, 100));

  useEffect(() => {
    window.addEventListener('resize', throttledUpdateStickyDataTable.current);
    return () => {
      window.removeEventListener('resize', throttledUpdateStickyDataTable.current);
    };
  }, []);

  useEffect(updateStickyDataTable, [columns, data]);

  const stickyColumns = columns.filter((column) => column.stickyLeft);
  const mainColumns = columns.map((col) => (
    col.stickyLeft ? { ...col, isDraggable: false } : col
  ));

  return (
    <div className={ cn(styles.stickyDataTableContainer, stickyDataTableContainer) }>
      <DataTable
        ref={ dataTableRef }
        { ...otherProps }
        dataTest={ dataTest }
        columns={ mainColumns }
        data={ data }
        scrollContainerClassName={ scrollContainerClassName }
      />
      { stickyColumns.length > 0 && (
        <div
          ref={ stickyWrapperRef }
          className={ cn(styles.stickyLeftTableContainer, stickyTableContainerClassName) }
        >
          <DataTable
            ref={ stickyDataTableRef }
            { ...otherProps }
            isHeaderDraggable={ false }
            dataTest={ `${ dataTest }Sticky` }
            columns={ stickyColumns }
            data={ data }
            scrollContainerClassName={ scrollContainerClassName ?? '' }
          />
        </div>
      ) }
      { shouldShowLoader({
        isFetching: otherProps.isFetching,
        isSkeletonEnabled: otherProps.skeleton?.isEnabled,
      }) && LoadingComponent && (
        <LoadingComponent overlay />
      ) }
    </div>
  );
};

export default StickyDataTable;
